import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase, callEdgeFunction } from '@/services/supabase';
import { Card, CardHeader, CardBody } from '@/components/common/Card';
import Button from '@/components/common/Button';
import PasswordConfirmationModal from '@/components/common/PasswordConfirmationModal';
import toast from 'react-hot-toast';

interface Offering {
  id: string;
  name: string;
  description: string | null;
  feature_key: string;
  stripe_price_id_monthly: string | null;
  stripe_price_id_yearly: string | null;
  price_monthly: string;
  price_yearly: string | null;
  icon_name: string;
  category: string;
  display_order: number;
  is_enabled: boolean;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

interface OfferingFormData {
  name: string;
  description: string;
  feature_key: string;
  stripe_price_id_monthly: string;
  stripe_price_id_yearly: string;
  price_monthly: string;
  price_yearly: string;
  icon_name: string;
  category: string;
  display_order: string;
  metadata: string;
}

const emptyFormData: OfferingFormData = {
  name: '',
  description: '',
  feature_key: '',
  stripe_price_id_monthly: '',
  stripe_price_id_yearly: '',
  price_monthly: '',
  price_yearly: '',
  icon_name: 'add-circle-outline',
  category: 'general',
  display_order: '0',
  metadata: '{}',
};

export default function ALaCarteOfferings() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<OfferingFormData>(emptyFormData);
  const [toggleTarget, setToggleTarget] = useState<Offering | null>(null);
  const [showAnnual, setShowAnnual] = useState(false);

  // Fetch offerings directly from DB (RLS allows admin SELECT)
  const { data: offerings, isLoading } = useQuery({
    queryKey: ['aLaCarteOfferings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('a_la_carte_offerings')
        .select('*')
        .order('display_order', { ascending: true });
      if (error) throw error;
      return data as Offering[];
    },
  });

  // Create offering
  const createMutation = useMutation({
    mutationFn: async (data: OfferingFormData) => {
      return callEdgeFunction('admin-manage-offerings', {
        action: 'create',
        name: data.name,
        description: data.description || null,
        feature_key: data.feature_key,
        stripe_price_id_monthly: data.stripe_price_id_monthly || null,
        stripe_price_id_yearly: data.stripe_price_id_yearly || null,
        price_monthly: parseFloat(data.price_monthly),
        price_yearly: data.price_yearly ? parseFloat(data.price_yearly) : null,
        icon_name: data.icon_name,
        category: data.category,
        display_order: parseInt(data.display_order, 10),
        metadata: JSON.parse(data.metadata || '{}'),
      });
    },
    onSuccess: () => {
      toast.success('Offering created');
      queryClient.invalidateQueries({ queryKey: ['aLaCarteOfferings'] });
      resetForm();
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to create offering');
    },
  });

  // Update offering
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: OfferingFormData }) => {
      return callEdgeFunction('admin-manage-offerings', {
        action: 'update',
        id,
        name: data.name,
        description: data.description || null,
        stripe_price_id_monthly: data.stripe_price_id_monthly || null,
        stripe_price_id_yearly: data.stripe_price_id_yearly || null,
        price_monthly: parseFloat(data.price_monthly),
        price_yearly: data.price_yearly ? parseFloat(data.price_yearly) : null,
        icon_name: data.icon_name,
        category: data.category,
        display_order: parseInt(data.display_order, 10),
        metadata: JSON.parse(data.metadata || '{}'),
      });
    },
    onSuccess: () => {
      toast.success('Offering updated');
      queryClient.invalidateQueries({ queryKey: ['aLaCarteOfferings'] });
      resetForm();
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to update offering');
    },
  });

  // Toggle offering (requires password)
  const toggleMutation = useMutation({
    mutationFn: async ({ id, password }: { id: string; password: string }) => {
      return callEdgeFunction('admin-manage-offerings', {
        action: 'toggle',
        id,
        password,
      });
    },
    onSuccess: () => {
      toast.success('Offering toggled successfully');
      queryClient.invalidateQueries({ queryKey: ['aLaCarteOfferings'] });
      setToggleTarget(null);
    },
    onError: (error) => {
      throw error; // Re-throw so PasswordConfirmationModal shows the error
    },
  });

  const resetForm = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData(emptyFormData);
    setShowAnnual(false);
  };

  const startEdit = (offering: Offering) => {
    setEditingId(offering.id);
    setFormData({
      name: offering.name,
      description: offering.description || '',
      feature_key: offering.feature_key,
      stripe_price_id_monthly: offering.stripe_price_id_monthly || '',
      stripe_price_id_yearly: offering.stripe_price_id_yearly || '',
      price_monthly: offering.price_monthly,
      price_yearly: offering.price_yearly || '',
      icon_name: offering.icon_name,
      category: offering.category,
      display_order: String(offering.display_order),
      metadata: JSON.stringify(offering.metadata, null, 2),
    });
    setShowAnnual(!!(offering.price_yearly || offering.stripe_price_id_yearly));
    setShowForm(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingId) {
      updateMutation.mutate({ id: editingId, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleToggleConfirm = async (password: string) => {
    if (!toggleTarget) return;
    await toggleMutation.mutateAsync({ id: toggleTarget.id, password });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">A La Carte Offerings</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage add-on offerings available for purchase on the a la carte screen
          </p>
        </div>
        <Button onClick={() => { resetForm(); setShowForm(true); }}>
          Add Offering
        </Button>
      </div>

      {/* Offerings Table */}
      <Card>
        <CardHeader>
          <h2 className="text-lg font-semibold">All Offerings</h2>
        </CardHeader>
        <CardBody>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <div className="w-8 h-8 border-2 border-brand border-t-transparent rounded-full animate-spin" />
            </div>
          ) : offerings && offerings.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Feature Key</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Monthly</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Yearly</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Enabled</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {offerings.map((offering) => (
                    <tr key={offering.id} className={!offering.is_enabled ? 'bg-gray-50 opacity-60' : ''}>
                      <td className="px-4 py-3 text-sm text-gray-500">{offering.display_order}</td>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900">{offering.name}</div>
                        {offering.description && (
                          <div className="text-xs text-gray-500 max-w-xs truncate">{offering.description}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <code className="text-xs bg-gray-100 px-2 py-1 rounded">{offering.feature_key}</code>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 capitalize">{offering.category}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">${parseFloat(offering.price_monthly).toFixed(2)}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {offering.price_yearly ? `$${parseFloat(offering.price_yearly).toFixed(2)}` : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => setToggleTarget(offering)}
                          role="switch"
                          aria-checked={offering.is_enabled}
                          aria-label={`${offering.is_enabled ? 'Disable' : 'Enable'} offering ${offering.name}`}
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                            offering.is_enabled ? 'bg-green-500' : 'bg-gray-300'
                          }`}
                        >
                          <span
                            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              offering.is_enabled ? 'translate-x-5' : 'translate-x-0'
                            }`}
                          />
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <Button variant="ghost" size="sm" onClick={() => startEdit(offering)}>
                          Edit
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No offerings configured yet</p>
          )}
        </CardBody>
      </Card>

      {/* Create/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <button
              type="button"
              className="fixed inset-0 bg-gray-500 bg-opacity-75"
              onClick={resetForm}
              aria-label="Close offering form"
            />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl p-6 z-10 max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {editingId ? 'Edit Offering' : 'Add New Offering'}
              </h3>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="offering-name" className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                    <input
                      id="offering-name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
                      required
                      aria-label="Offering name"
                    />
                  </div>
                  <div>
                    <label htmlFor="offering-feature-key" className="block text-sm font-medium text-gray-700 mb-1">Feature Key *</label>
                    <input
                      id="offering-feature-key"
                      name="featureKey"
                      type="text"
                      value={formData.feature_key}
                      onChange={(e) => setFormData({ ...formData, feature_key: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand font-mono text-sm"
                      placeholder="e.g., realtime_translation"
                      required
                      disabled={!!editingId}
                      aria-label="Feature key"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="offering-description" className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    id="offering-description"
                    name="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
                    rows={2}
                    aria-label="Description"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="offering-price-monthly" className="block text-sm font-medium text-gray-700 mb-1">Monthly Price ($) *</label>
                    <input
                      id="offering-price-monthly"
                      name="monthlyPrice"
                      type="number"
                      step="0.01"
                      value={formData.price_monthly}
                      onChange={(e) => setFormData({ ...formData, price_monthly: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
                      required
                      aria-label="Monthly price"
                    />
                  </div>
                  <div>
                    <label htmlFor="offering-stripe-monthly-id" className="block text-sm font-medium text-gray-700 mb-1">Stripe Monthly Price ID</label>
                    <input
                      id="offering-stripe-monthly-id"
                      name="stripeMonthlyPriceId"
                      type="text"
                      value={formData.stripe_price_id_monthly}
                      onChange={(e) => setFormData({ ...formData, stripe_price_id_monthly: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand font-mono text-sm"
                      placeholder="price_..."
                      aria-label="Stripe monthly price ID"
                    />
                  </div>
                </div>

                <label htmlFor="offering-enable-annual" className="flex items-center gap-2 cursor-pointer">
                  <input
                    id="offering-enable-annual"
                    name="enableAnnualPricing"
                    type="checkbox"
                    checked={showAnnual}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setShowAnnual(checked);
                      if (!checked) {
                        setFormData({ ...formData, price_yearly: '', stripe_price_id_yearly: '' });
                      }
                    }}
                    className="h-4 w-4 rounded border-gray-300 text-brand focus:ring-brand"
                    aria-label="Enable annual pricing"
                  />
                  <span className="text-sm font-medium text-gray-700">Enable Annual Pricing</span>
                </label>

                {showAnnual && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="offering-price-yearly" className="block text-sm font-medium text-gray-700 mb-1">Yearly Price ($)</label>
                      <input
                        id="offering-price-yearly"
                        name="yearlyPrice"
                        type="number"
                        step="0.01"
                        value={formData.price_yearly}
                        onChange={(e) => setFormData({ ...formData, price_yearly: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
                        aria-label="Yearly price"
                      />
                    </div>
                    <div>
                      <label htmlFor="offering-stripe-yearly-id" className="block text-sm font-medium text-gray-700 mb-1">Stripe Yearly Price ID</label>
                      <input
                        id="offering-stripe-yearly-id"
                        name="stripeYearlyPriceId"
                        type="text"
                        value={formData.stripe_price_id_yearly}
                        onChange={(e) => setFormData({ ...formData, stripe_price_id_yearly: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand font-mono text-sm"
                        placeholder="price_..."
                        aria-label="Stripe yearly price ID"
                      />
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label htmlFor="offering-icon-name" className="block text-sm font-medium text-gray-700 mb-1">Icon Name</label>
                    <input
                      id="offering-icon-name"
                      name="iconName"
                      type="text"
                      value={formData.icon_name}
                      onChange={(e) => setFormData({ ...formData, icon_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand text-sm"
                      placeholder="Ionicons name"
                      aria-label="Icon name"
                    />
                  </div>
                  <div>
                    <label htmlFor="offering-category" className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                    <select
                      id="offering-category"
                      name="category"
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
                      aria-label="Category"
                    >
                      <option value="general">General</option>
                      <option value="communication">Communication</option>
                      <option value="video">Video</option>
                      <option value="analytics">Analytics</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="offering-display-order" className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
                    <input
                      id="offering-display-order"
                      name="displayOrder"
                      type="number"
                      value={formData.display_order}
                      onChange={(e) => setFormData({ ...formData, display_order: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
                      aria-label="Display order"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="offering-metadata" className="block text-sm font-medium text-gray-700 mb-1">Metadata (JSON)</label>
                  <textarea
                    id="offering-metadata"
                    name="metadata"
                    value={formData.metadata}
                    onChange={(e) => setFormData({ ...formData, metadata: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand font-mono text-sm"
                    rows={3}
                    aria-label="Metadata JSON"
                  />
                </div>

                <div className="flex gap-3 justify-end pt-2">
                  <Button variant="secondary" onClick={resetForm}>Cancel</Button>
                  <Button
                    type="submit"
                    loading={createMutation.isPending || updateMutation.isPending}
                  >
                    {editingId ? 'Save Changes' : 'Create Offering'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Password Confirmation Modal for Toggle */}
      <PasswordConfirmationModal
        isOpen={!!toggleTarget}
        title={toggleTarget?.is_enabled ? 'Disable Offering' : 'Enable Offering'}
        message={
          toggleTarget?.is_enabled
            ? `Are you sure you want to disable "${toggleTarget?.name}"? It will no longer appear on the a la carte screen for new purchases. Existing purchases will remain active.`
            : `Are you sure you want to enable "${toggleTarget?.name}"? It will become available for purchase on the a la carte screen.`
        }
        onConfirm={handleToggleConfirm}
        onCancel={() => setToggleTarget(null)}
        confirmLabel={toggleTarget?.is_enabled ? 'Disable' : 'Enable'}
        confirmVariant={toggleTarget?.is_enabled ? 'danger' : 'primary'}
      />
    </div>
  );
}
